import React from "react";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { BereichKey, BereichOption, Modus } from "../../../../types/zugriff";
import {
  alsOberpunkte,
  schreibZustand,
  zustand,
} from "../../../../utils/zugriff";
import { colors } from "../../../../styles/theme";

/**
 * Breite der Schreiben-Spalte und der Spalte des Auf-/Zuklapp-Pfeils.
 *
 * Beide stehen hier als Konstante, weil Kopfzeile, Oberpunkt- und Blattzeile dieselben Werte
 * brauchen: Nur dann sitzen die Schreiben-Häkchen senkrecht untereinander und die Überschrift
 * genau darüber. Der Pfeil bekommt eine feste Spalte, weil ein `IconButton` schmaler ist als
 * der Platzhalter der Blattzeilen – die vier Pixel Unterschied hatten die Spalte verschoben.
 */
const SPALTE_SCHREIBEN = 72;
const SPALTE_PFEIL = 34;

interface BereichBaumProps {
  katalog: BereichOption[];
  /** Ausgewählte Bereiche mit ihrem Modus. Nicht enthaltene Bereiche sind nicht freigegeben. */
  auswahl: Map<BereichKey, Modus>;
  onChange: (auswahl: Map<BereichKey, Modus>) => void;
  disabled?: boolean;
}

/**
 * Der Freigabe-Baum: links das Häkchen „freigegeben", rechts die schmale Spalte „Schreiben".
 *
 * Lesend ist der Normalfall und wird nicht eigens angezeigt – ein gesetztes Häkchen bedeutet
 * lesenden Zugriff. Schreiben ist die zusätzliche Option und anfangs leer; sie lässt sich nur
 * dort setzen, wo überhaupt freigegeben wurde.
 *
 * Oberpunkte zeigen einen Teil-Zustand, wenn nur einzelne Unterpunkte gewählt sind.
 */
const BereichBaum: React.FC<BereichBaumProps> = ({
  katalog,
  auswahl,
  onChange,
  disabled = false,
}) => {
  const gruppen = alsOberpunkte(katalog);
  const [eingeklappt, setEingeklappt] = React.useState<Set<string>>(new Set());

  const aendern = (f: (naechste: Map<BereichKey, Modus>) => void) => {
    const naechste = new Map(auswahl);
    f(naechste);
    onChange(naechste);
  };

  const blattUmschalten = (key: BereichKey) =>
    aendern((n) => {
      if (n.has(key)) n.delete(key);
      else n.set(key, "lesen");
    });

  const schreibenUmschalten = (key: BereichKey) =>
    aendern((n) => {
      if (!n.has(key)) return;
      n.set(key, n.get(key) === "schreiben" ? "lesen" : "schreiben");
    });

  const gruppeUmschalten = (blaetter: BereichOption[]) =>
    aendern((n) => {
      const vollstaendig = zustand(blaetter, auswahl) === "voll";
      blaetter.forEach((b) =>
        vollstaendig ? n.delete(b.key) : n.set(b.key, n.get(b.key) ?? "lesen"),
      );
    });

  const gruppeSchreibenUmschalten = (blaetter: BereichOption[]) =>
    aendern((n) => {
      const alleSchreibend = schreibZustand(blaetter, auswahl) === "voll";
      blaetter
        .filter((b) => n.has(b.key))
        .forEach((b) => n.set(b.key, alleSchreibend ? "lesen" : "schreiben"));
    });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", pb: 0.5, pr: 1 }}>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            width: SPALTE_SCHREIBEN,
            textAlign: "center",
          }}
        >
          Schreiben
        </Typography>
        <Box sx={{ width: SPALTE_PFEIL }} />
      </Box>

      {gruppen.map((gruppe) => {
        const istEingeklappt = eingeklappt.has(gruppe.key);
        const freigabe = zustand(gruppe.blaetter, auswahl);
        const schreiben = schreibZustand(gruppe.blaetter, auswahl);
        const nichtsFreigegeben = freigabe === "leer";

        return (
          <Box key={gruppe.key} sx={{ mb: 0.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: colors.background.tableHeader,
                borderRadius: 1,
                pr: 1,
              }}
            >
              <Checkbox
                checked={freigabe === "voll"}
                indeterminate={freigabe === "teilweise"}
                onChange={() => gruppeUmschalten(gruppe.blaetter)}
                disabled={disabled}
                size="small"
              />
              <Typography sx={{ flex: 1, fontWeight: 600 }}>
                {gruppe.label}
              </Typography>

              <Box sx={{ width: SPALTE_SCHREIBEN, textAlign: "center" }}>
                <Tooltip
                  title={
                    nichtsFreigegeben
                      ? "Erst einen Bereich freigeben"
                      : "Schreibrechte für dieses Modul"
                  }
                >
                  <span>
                    <Checkbox
                      checked={schreiben === "voll"}
                      indeterminate={schreiben === "teilweise"}
                      onChange={() =>
                        gruppeSchreibenUmschalten(gruppe.blaetter)
                      }
                      disabled={disabled || nichtsFreigegeben}
                      size="small"
                    />
                  </span>
                </Tooltip>
              </Box>

              <Box
                sx={{
                  width: SPALTE_PFEIL,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setEingeklappt((vorher) => {
                      const naechste = new Set(vorher);
                      if (naechste.has(gruppe.key)) naechste.delete(gruppe.key);
                      else naechste.add(gruppe.key);
                      return naechste;
                    })
                  }
                >
                  {istEingeklappt ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ExpandLessIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Box>

            <Collapse in={!istEingeklappt}>
              {gruppe.blaetter.map((blatt) => {
                const freigegeben = auswahl.has(blatt.key);
                return (
                  <Box
                    key={blatt.key}
                    sx={{ display: "flex", alignItems: "center", pl: 3, pr: 1 }}
                  >
                    <Checkbox
                      checked={freigegeben}
                      onChange={() => blattUmschalten(blatt.key)}
                      disabled={disabled}
                      size="small"
                    />
                    <Typography sx={{ flex: 1 }}>{blatt.label}</Typography>
                    <Box sx={{ width: SPALTE_SCHREIBEN, textAlign: "center" }}>
                      <Checkbox
                        checked={auswahl.get(blatt.key) === "schreiben"}
                        onChange={() => schreibenUmschalten(blatt.key)}
                        disabled={disabled || !freigegeben}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ width: SPALTE_PFEIL }} />
                  </Box>
                );
              })}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default BereichBaum;
